import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-register-user',
  templateUrl: './register-user.component.html',
  styleUrls: ['./register-user.component.scss']
})
export class RegisterUserComponent implements OnInit {
  @Input() storeId: string = '';
  
  registerForm!: FormGroup;
  loading = false;
  existingUser: any = null;
  countryCodes = ['+1', '+44', '+91', '+34', '+49']; // Add more as needed
  selectedStore: any;
  
  // Track the current step in the registration flow
  currentStep: 'register' | 'linkOptions' | 'storeSelection' | 'nfcReading' = 'register';
  
  // User data after successful registration or selection
  userData: any = null;
  
  // For store selection
  stores: any[] = [];
  visitorStoreId: string = '';
  userRole: string | null = localStorage.getItem('role');
  managerId: string | null = localStorage.getItem('managerId');
  
  // For NFC reading
  nfcSupported = false;
  nfcReading = false;
  nfcError: string | undefined = undefined;
  scannedCardUID: string | null = null;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private dialogService: NbDialogService,
    public dialogRef: NbDialogRef<RegisterUserComponent>
  ) {}

  ngOnInit() {
    this.initForm();
    this.loadSelectedStore();
    // Check if NFC is supported
    this.checkNfcSupport();
    // Load available stores for the current user
    this.loadAvailableStores();
  }

  initForm() {
    this.registerForm = this.fb.group({
      fullName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      countryCode: ['+45', [Validators.required, Validators.pattern(/^\+\d{1,3}$/)]],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9]{8,15}$/)]],
      dob: [''],
      age: [''],
      cprNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]]
    });
    
    // Add listeners to check for existing users
    this.registerForm.get('email')?.valueChanges.subscribe(value => {
      if (value && this.registerForm.get('email')?.valid) {
        this.checkExistingUser();
      }
    });
    
    this.registerForm.get('phone')?.valueChanges.subscribe(value => {
      if (value && value.length > 8) {
        this.checkExistingUser();
      }
    });

    // Ensure country code format
    this.registerForm.get('countryCode')?.valueChanges.subscribe(value => {
      if (value && !value.startsWith('+')) {
        this.registerForm.get('countryCode')?.setValue('+' + value);
      }
    });
  }

  loadSelectedStore() {
    // Get the current selected store
    if (this.storeId) {
      this.apiService.getRequest(`${ApiUrl.storeById}${this.storeId}`).subscribe({
        next: (res: any) => {
          // Use the standardized response handling approach
          const responseData = res.response || res;
          
          if (responseData.success) {
            this.selectedStore = responseData.data;
          }
        },
        error: (err: any) => {
          console.error('Error loading store:', err);
          
          // Check for wrapped error response
          const errorResponse = err.error?.response || err.error;
          console.error('Error details:', errorResponse?.message || 'Unknown error');
        }
      });
    }
  }

  checkExistingUser() {
    const email = this.registerForm.get('email')?.value;
    const phone = this.registerForm.get('phone')?.value;
    
    if ((!email && !phone) || !this.registerForm.get('email')?.valid) {
      return;
    }

    this.loading = true;
    this.spinner.show();
    
    // Use a more targeted approach with email as the primary search field
    this.searchUserDirectly(email, phone);
  }

  // New method for direct user search
  searchUserDirectly(email: string, phone?: string, isFromError: boolean = false) {
    if (!email && !phone) {
      this.loading = false;
      this.spinner.hide();
      return;
    }
    
    // Build a query that specifically limits the search
    const params = new URLSearchParams();
    
    if (email) {
      // Use exact email match
      params.append('email', email);
    }
    
    if (phone) {
      // Add phone as secondary search parameter
      params.append('phone', phone);
    }
    
    // Limit to just 1 result since we only need to know if the user exists
    params.append('limit', '1');
    
    const searchUrl = `${ApiUrl.getUserList}?${params.toString()}`;
    
    console.log('Searching for user with:', { email, phone, isFromError });
    
    this.apiService.getRequest(searchUrl).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.spinner.hide();
        
        // Handle nested response structure, checking both the standard format and wrapped response
        const responseData = res.response || res;
        
        // Check if we have user data in the correct format
        if (responseData.success && 
            responseData.data && 
            responseData.data.userData && 
            responseData.data.userData.length > 0) {
          
          this.existingUser = responseData.data.userData[0];
          
          // Different message based on context
          if (isFromError) {
            this.toastr.warning('This email is already registered in the system', 'User Found');
          } else {
            this.toastr.warning('User already exists', 'Warning');
          }
        } else {
          this.existingUser = null;
          
          // Only show error message if we're coming from an API error
          if (isFromError) {
            this.toastr.error('Email already exists but could not find the user', 'Error');
          }
        }
      },
      error: (err: any) => {
        this.loading = false;
        this.spinner.hide();
        console.error('Error checking user:', err);
        
        // Check for wrapped error response
        const errorResponse = err.error?.response || err.error;
        this.toastr.error(errorResponse?.message || 'Error checking for existing user', 'Error');
      }
    });
  }

  selectExistingUser() {
    // Store the selected user and move to the next step
    this.userData = this.existingUser;
    
    // Always go to store selection first to confirm where the visit is happening
    this.currentStep = 'storeSelection';
    
    // Make a fresh API call to load stores for the dropdown 
    // whenever we reach the store selection step
    this.loadStoresForStepSelection();
    
    // Pre-select the current store if available
    if (this.storeId) {
      this.visitorStoreId = this.storeId;
    } else if (this.stores.length === 1 && this.stores[0]._id) {
      this.visitorStoreId = this.stores[0]._id;
    }
  }

  calculateAge(selectedDate: Date | null) {
    console.log('calculateAge called with date:', selectedDate);

    if (selectedDate) {
      const birthDate = new Date(selectedDate);

      // Check if the parsed date is valid
      if (isNaN(birthDate.getTime())) { 
        console.error('Invalid date received:', selectedDate);
        this.registerForm.patchValue({ age: '' });
        return; 
      }

      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      // Ensure age is not negative
      age = Math.max(0, age); 
      
      console.log('Calculated age:', age);
      this.registerForm.patchValue({ age: age.toString() });
    } else {
      console.log('No date selected, clearing age');
      this.registerForm.patchValue({ age: '' });
    }
  }

  onSubmit() {
    if (this.registerForm.invalid) {
      Object.keys(this.registerForm.controls).forEach(key => {
        const control = this.registerForm.get(key);
        control?.markAsTouched();
        control?.markAsDirty();
      });
      return;
    }

    this.loading = true;
    this.spinner.show();
    
    const userData = {
      name: this.registerForm.value.fullName,
      email: this.registerForm.value.email,
      cprNumber: this.registerForm.value.cprNumber,
      phone: this.registerForm.value.phone, // Send only the phone number without country code
      countryCode: this.registerForm.value.countryCode, // Send country code as separate field
      dateofbirth: this.registerForm.value.dob,
      age: this.registerForm.value.age,
      storeId: this.storeId
    };
    
    // Use the actual API endpoint to register user
    this.apiService.postRequest(ApiUrl.addUser, userData).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.spinner.hide();
        
        // Check for the response wrapper format first
        const responseData = res.response || res;
        
        if (responseData.success) {
          this.toastr.success(responseData.message || 'User registered successfully', 'Success');
          
          // Store the new user data and move to the next step
          // Make sure we're getting the data from the correct path in the response
          this.userData = responseData.data;
          
          // Always go to store selection first to confirm where the visit is happening
          this.currentStep = 'storeSelection';
          
          // Make a fresh API call to load stores for the dropdown
          this.loadStoresForStepSelection();
          
          // Pre-select the current store if available
          if (this.storeId) {
            this.visitorStoreId = this.storeId;
          } else if (this.stores.length === 1 && this.stores[0]._id) {
            this.visitorStoreId = this.stores[0]._id;
          }
        } else {
          if (responseData.message === 'Email Already Exists') {
            // If email already exists, search for the user
            this.searchExistingUserByEmail(userData.email);
          } else {
            this.toastr.warning(responseData.message || 'Failed to register user', 'Warning');
          }
        }
      },
      error: (err: any) => {
        this.loading = false;
        this.spinner.hide();
        console.error('Registration error:', err);
        
        // Also check for wrapped error response
        const errorResponse = err.error?.response || err.error;
        
        // Check if the error is about an existing email
        if (errorResponse?.message === 'Email Already Exists') {
          // If email already exists, search for the user
          this.searchExistingUserByEmail(userData.email);
        } else {
          this.toastr.error(errorResponse?.message || 'Error registering user', 'Error');
        }
      }
    });
  }
  
  // Search for a user by email when we get an "Email Already Exists" error
  searchExistingUserByEmail(email: string) {
    if (!email) {
      this.toastr.error('Email is required to search for existing user', 'Error');
      return;
    }
    
    this.loading = true;
    this.spinner.show();
    
    // Use the direct search method with just the email, indicating this is from an error
    this.searchUserDirectly(email, undefined, true);
  }

  // Method to record store visit and then open card link dialog
  openCardLinkDialog() {
    if (!this.userData || !this.userData._id) {
      this.toastr.error('User data is missing', 'Error');
      return;
    }

    if (!this.visitorStoreId) {
      this.toastr.error('Store selection is required', 'Error');
      return;
    }
    
    this.loading = true;
    this.spinner.show();
    
    // First record the store visit
    const visitData = {
      userId: this.userData._id,
      storeId: this.visitorStoreId
    };
    
    this.apiService.postRequest(ApiUrl.createStoreVisit, visitData).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.spinner.hide();
        
        // Use the standardized response handling approach
        const responseData = res.response || res;
        
        if (responseData.success) {
          console.log('Store visit recorded:', responseData.data);
          
          // Show success message
          this.toastr.success('Store visit recorded. You can now select a card from the Card UID Management panel.', 'Success');
          
          // Close this dialog with the user data for reference
          this.dialogRef.close({
            success: true,
            userData: this.userData,
            message: 'Store visit recorded. Please select a card UID to link to this user.'
          });
        } else {
          this.toastr.error(responseData.message || 'Failed to record store visit');
        }
      },
      error: (err: any) => {
        this.loading = false;
        this.spinner.hide();
        console.error('Error recording store visit:', err);
        
        // Check for wrapped error response
        const errorResponse = err.error?.response || err.error;
        this.toastr.error(errorResponse?.message || 'Failed to record store visit');
      }
    });
  }

  // Method to confirm store selection and proceed to card linking options
  confirmStoreSelection() {
    if (!this.visitorStoreId) {
      this.toastr.error('Please select a store', 'Error');
      return;
    }
    
    this.currentStep = 'linkOptions';
  }

  // Method to start NFC reading
  startNfcReading() {
    if (!this.nfcSupported) {
      this.toastr.error('NFC is not supported on this device', 'Error');
      return;
    }

    if (!this.userData || !this.userData._id) {
      this.toastr.error('User data is missing', 'Error');
      return;
    }

    if (!this.visitorStoreId) {
      this.toastr.error('Store selection is required', 'Error');
      return;
    }
    
    this.loading = true;
    this.spinner.show();
    
    // First record the store visit
    const visitData = {
      userId: this.userData._id,
      storeId: this.visitorStoreId
    };
    
    this.apiService.postRequest(ApiUrl.createStoreVisit, visitData).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.spinner.hide();
        
        // Use the standardized response handling approach
        const responseData = res.response || res;
        
        if (responseData.success) {
          console.log('Store visit recorded:', responseData.data);
          
          // Now proceed with NFC reading
          this.currentStep = 'nfcReading';
          this.nfcReading = true;
          this.nfcError = undefined;
          this.scannedCardUID = null;

          // @ts-ignore: NDEFReader is not in the TypeScript types yet
          const ndef = new window.NDEFReader();
          ndef.scan()
            .then(() => {
              this.toastr.info('Scan started! Please tap your NFC card', 'NFC Reader');
              
              ndef.addEventListener("reading", ({ serialNumber }: { serialNumber: string }) => {
                this.scannedCardUID = serialNumber;
                this.nfcReading = false;
                
                // Process the scanned card UID
                if (this.scannedCardUID && this.userData) {
                  this.linkCardToUser(this.scannedCardUID, this.userData._id);
                }
              });
            })
            .catch((error: any) => {
              this.nfcError = error.message || 'Failed to start NFC scan';
              this.nfcReading = false;
              this.toastr.error(this.nfcError, 'NFC Error');
              console.error('NFC error:', error);
            });
        } else {
          this.toastr.error(responseData.message || 'Failed to record store visit');
        }
      },
      error: (err: any) => {
        this.loading = false;
        this.spinner.hide();
        console.error('Error recording store visit:', err);
        
        // Check for wrapped error response
        const errorResponse = err.error?.response || err.error;
        this.toastr.error(errorResponse?.message || 'Failed to record store visit');
      }
    });
  }

  // Method to cancel NFC reading and go back to options
  cancelNfcReading() {
    this.nfcReading = false;
    this.currentStep = 'linkOptions';
  }

  // Method to link card directly to user after NFC scan
  linkCardToUser(cardUID: string | null, userId: string) {
    if (!cardUID || !userId || !this.storeId) {
      this.toastr.error('Missing required information for card linking', 'Error');
      return;
    }

    this.loading = true;
    this.spinner.show();

    // Use non-null assertion since we already checked above that cardUID is not null
    const cardUIDString: string = cardUID!;
    
    const requestData = {
      cardUID: cardUIDString,
      userId: userId,
      storeId: this.storeId,
      // We don't need signature for direct NFC reading as we're in the device
      skipSignature: true
    };

    this.apiService.postRequest(ApiUrl.linkCardToUser, requestData).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.spinner.hide();
        
        // Use the standardized response handling approach
        const responseData = res.response || res;
        
        if (responseData.success) {
          this.toastr.success(responseData.message || 'Card successfully linked to user account', 'Success');
          // Close dialog with success result
          this.dialogRef.close(true);
        } else {
          this.toastr.error(responseData.message || 'Failed to link card to user', 'Error');
        }
      },
      error: (err: any) => {
        this.loading = false;
        this.spinner.hide();
        console.error('Error linking card:', err);
        
        // Check for wrapped error response
        const errorResponse = err.error?.response || err.error;
        this.toastr.error(errorResponse?.message || 'Failed to link card to user', 'Error');
      }
    });
  }

  // Go back to registration form from link options or store selection
  backToRegistration() {
    this.currentStep = 'register';
    this.userData = null;
  }
  
  // Go back from store selection to previous step
  backFromStoreSelection() {
    if (this.userData) {
      this.currentStep = 'register';
    } else {
      this.cancel();
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  onBlur(field: string) {
    if (field === 'email' || field === 'phone') {
      this.checkExistingUser();
    } else if (field === 'dob') {
      this.calculateAge(this.registerForm.get('dob')?.value);
    }
  }

  // Check if the browser supports NFC
  checkNfcSupport() {
    if ('NDEFReader' in window) {
      this.nfcSupported = true;
      console.log('NFC is supported on this device');
    } else {
      this.nfcSupported = false;
      console.log('NFC is not supported on this device');
    }
  }

  // Load available stores for the current user
  loadAvailableStores() {
    console.log('Loading available stores with improved method');
    
    this.loading = true;
    this.spinner.show();
    
    const managerId = localStorage.getItem('managerId');
    const role = localStorage.getItem('role');
    
    // Get stores based on user role:
    // - For admins: get all stores
    // - For managers: get only stores they are authorized to see
    let endpoint;
    if (role === 'manager' && managerId) {
      // Manager can only see stores they are authorized for
      endpoint = `${ApiUrl.storeListManagerDropdown}${managerId}`;
      console.log('Using manager endpoint with managerId:', managerId);
    } else {
      // Admin can see all stores
      endpoint = ApiUrl.storeListAdminDropdown;
      console.log('Using admin endpoint for all stores');
    }
    
    console.log('Requesting stores from endpoint:', endpoint);
    
    this.apiService.getRequest(endpoint).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.spinner.hide();
        
        console.log('Store API response:', res);
        const responseData = res.response || res;
        
        if (responseData.success) {
          // Different response structure for manager vs admin
          if (role === 'manager') {
            // For managers, data comes directly in the data field
            this.stores = responseData.data || [];
            console.log('Loaded stores for manager:', this.stores.length);
          } else {
            // For admin users, data may be in different formats
            if (responseData.data?.storesData) {
              this.stores = responseData.data.storesData || [];
            } else if (Array.isArray(responseData.data)) {
              this.stores = responseData.data;
            } else {
              this.stores = [];
              console.error('Unexpected store data format:', responseData.data);
            }
            console.log('Loaded stores for admin:', this.stores.length);
          }
          
          // Ensure each store has _id and name properties
          this.stores = this.stores.map(store => {
            // If store is just an ID string, convert it to an object with proper properties
            if (typeof store === 'string') {
              return { _id: store, name: `Store ${store.substr(0, 8)}...` };
            }
            
            // If store has id but not _id, normalize it
            if (store.id && !store._id) {
              store._id = store.id;
            }
            
            // If store has no name, use a placeholder or try alternative properties
            if (!store.name) {
              if (store.storeName) {
                store.name = store.storeName;
              } else if (store.store_name) {
                store.name = store.store_name;
              } else {
                store.name = `Store ${store._id.substr(0, 8)}...`;
              }
            }
            
            return store;
          });
          
          console.log('Processed stores:', this.stores);
          
          // Pre-select the store if available
          if (this.storeId) {
            this.visitorStoreId = this.storeId;
            console.log('Pre-selected the specified store:', this.storeId);
          } else if (this.stores.length === 1) {
            this.visitorStoreId = this.stores[0]._id;
            console.log('Pre-selected the only available store:', this.visitorStoreId);
          }
        } else {
          this.toastr.error(responseData.message || 'Failed to load stores', 'Error');
          console.error('Error loading stores:', responseData.message);
        }
      },
      error: (err: any) => {
        this.loading = false;
        this.spinner.hide();
        
        // Check for wrapped error response
        const errorResponse = err.error?.response || err.error;
        this.toastr.error(errorResponse?.message || 'Failed to load stores', 'Error');
        console.error('API error loading stores:', err);
        
        // Fallback to test stores if in development environment
        if (!environment.production) {
          this.loadTestStores();
        }
      }
    });
  }
  
  // Method to directly load test stores when API fails (development only)
  loadTestStores() {
    if (environment.production) {
      console.warn('Test stores should not be used in production');
      this.toastr.warning('This function is only for development environments', 'Warning');
      return;
    }
    
    console.log('Loading test stores for development');
    
    // Create test store data
    this.stores = [
      {
        _id: 'test-store-1',
        name: 'Test Store Madrid',
        address: 'Calle Gran Vía 1, Madrid'
      },
      {
        _id: 'test-store-2',
        name: 'Test Store Barcelona',
        address: 'La Rambla 123, Barcelona'
      },
      {
        _id: 'test-store-3',
        name: 'Test Store Valencia',
        address: 'Plaza del Ayuntamiento 1, Valencia'
      }
    ];
    
    this.toastr.success('Loaded test stores for development', 'Success');
    console.log('Test stores loaded:', this.stores);
  }
  
  // Helper method to safely get store name from ID
  getStoreName(storeId: string): string {
    if (!storeId || !this.stores || this.stores.length === 0) {
      return storeId;
    }
    
    const foundStore = this.stores.find(store => store._id === storeId);
    return foundStore?.name || storeId;
  }
  
  // Dedicated method to load stores specifically for the store selection step
  // Using the same method to ensure consistency
  loadStoresForStepSelection() {
    console.log('Loading stores for the store selection step');
    // Clear existing stores to ensure we don't show stale data
    this.stores = [];
    this.visitorStoreId = '';
    
    // Use the same method for consistency
    this.loadAvailableStores();
  }

  onPhoneInput(event: any) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '').slice(0, 20);
    this.registerForm.get('phone')?.setValue(input.value);
  }

  onCountryCodeInput(event: any) {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    
    // Ensure it starts with +
    if (!value.startsWith('+')) {
      value = '+' + value.replace(/\+/g, '');
    }
    
    // Remove any non-digit characters except the leading +
    value = '+' + value.substr(1).replace(/[^\d]/g, '');
    
    // Limit to 4 characters (+XXX)
    value = value.slice(0, 4);
    
    this.registerForm.get('countryCode')?.setValue(value);
  }
} 